import RestClient from './RestClient';
import API_ENDPOINTS from './apiConfig';
import getEndPoint from './hubClient/apiConstants';

import config from './config';
import Storage from './storage';
import Cookies from 'universal-cookie';

export default class AuthManager {
  /**
   * gets current logged in user from backend and stores in local storage
   * @returns
   */
  static async initialize() {
    if (this.isLoggedIn()) {
      try {
        const response = await RestClient.get(API_ENDPOINTS.AUTH.CURRENT_USER);
        return response;
      } catch (e) {
      }
    }
  }

  static isLoggedIn() {
    return !!this.getAppToken();
  }

  static getUser() {
    return JSON.parse(localStorage.getItem('user'));
  }

  static async logout(redirectTo = '/app') {
    await RestClient.request(API_ENDPOINTS.AUTH.LOGOUT, { method: 'DELETE' });
    this.removeFullToken();
    this.removeUserFromLocalStorage();
    this.removeUserIdFromLocalStorage();
    if (redirectTo) {
      window.location = redirectTo;
    }
  }

  static persistProductAccountsToCookies(productAccounts) {
    if (productAccounts) {
      let cookies = new Cookies();
      let options = {
        domain: 'payu.in',
        path: '/',
        secure: true
      };
      try {
        const requiredDetailsOfProductAccounts = productAccounts.map(({
          type,
          uuid,
          merchant_account_uuid,
          identifier,
        }) => ({
          product_account_type: type,
          product_account_uuid: uuid,
          merchant_account_uuid,
          identifier,
        }))
        cookies.set('product_accounts', JSON.stringify(requiredDetailsOfProductAccounts), options);
      } catch {
        console.log('error: 001')
      }
    }
  }

  static persistUserToLocalStorage(user) {
    let cookies = new Cookies();
    let tokenInfo = JSON.parse(localStorage.getItem('tokenData'));
    if (user && user.mid && user.product && tokenInfo) {
      let options = {
        domain: 'payu.in',
        expires: new Date(tokenInfo['tokenLifeTime']),
        path: '/',
        secure: true
      };
      cookies.set('mid', user.mid.toString(), options);
      cookies.set('product', user.product.toUpperCase(), options);
      cookies.set('merchantAccessToken', tokenInfo['access_token'], options);
      cookies.set('dashboardPreference', user.dashboard_preference, options);
      cookies.set('product_account_uuid', user.product_account_uuid, options);
      cookies.set('product_account_type', user.type, options);
      cookies.set('merchant_account_uuid', user.merchant_account_uuid, options);
      if (user.product.toUpperCase() === 'PAYUBIZ' && user.business_origin) {
        // set merchantType expiry-time 1 month from the time when it is set
        let date = new Date();
        date.setMonth(date.getMonth() + 1);
        options.expires = date;
        cookies.set('merchantType', user.business_origin, options);
      }

    }
    localStorage.setItem('user', JSON.stringify(user));
  }

  // TODO: reseller_uuid should not be stored in local storage
  // https://payuindia.atlassian.net/browse/PPP-207

  static persistUserIdToLocalStorage(userUuid) {
    localStorage.setItem('user_uuid', JSON.stringify(userUuid));
  }

  static getUserUUID() {
    try {
      return JSON.parse(window.localStorage.getItem('user_uuid'));
    } catch (e) {
      return null;
    }
  }

  static removeUserFromLocalStorage() {
    localStorage.removeItem('user');
  }

  static removeUserIdFromLocalStorage() {
    localStorage.removeItem('user_uuid');
  }

  static getHubToken(hubUser) {
    if (hubUser && hubUser.limitedToken) {
      const { limitedToken: token } = hubUser;
      return token['access-token'];
    }
    return null;
  }

  static persistFullToken(tokenData) {
    let data;
    try {
      const tokenLifeTime = new Date();
      tokenLifeTime.setSeconds(tokenData.expires_in);
      tokenData.tokenLifeTime = tokenLifeTime;
      data = JSON.stringify(tokenData);
      /**
       * setting cookies here because if authToken gets expired
       * then CO was not setting authToken in cookies because fullToken was already present in localStorage
       */
      let cookies = new Cookies();
      const options = {
        domain: 'payu.in',
        expires: tokenData['tokenLifeTime'],
        path: '/',
        secure: true
      };
      cookies.set('merchantAccessToken', tokenData['access_token'], options);
    } catch (e) {
      console.warn(e);
    }
    // if (!this.getFullToken()) {
      window.localStorage.setItem('tempToken', data);
    // }
  }

  /**
   * set temp token data with app token
   * tempData contains signin token but user will be considered loggedin
   * if token is contained in tokenData. This is done to that user must
   * complete login flow to be loggedin
   */
  static setAppToken() {
    let cookies = new Cookies();
    cookies.getAll() && cookies.remove('token');
    const tempToken = window.localStorage.getItem('tempToken');
    if (tempToken) {
      window.localStorage.setItem('tokenData', tempToken);
      cookies.set('token', JSON.parse(localStorage.tokenData)['access_token'], { path: '/' });
    }
    window.localStorage.removeItem('tempToken');
    window.localStorage.removeItem('hubToken');
  }

  static getAppToken() {
    try {
      return JSON.parse(window.localStorage.getItem('tokenData')).access_token;
    } catch (e) {
      return null;
    }
  }

  static getFullToken() {
    try {
      return JSON.parse(window.localStorage.getItem('tempToken')).access_token;
    } catch (e) {
      return null;
    }
  }

  static getUserId() {
    try {
      return JSON.parse(window.localStorage.getItem('tempToken')).user_uuid;
    } catch (e) {
      return null;
    }
  }

  static removeFullToken() {
    return window.localStorage.removeItem('tempToken');
  }

  static getHubApiToken() {
    try {
      return Storage.getItem('hubToken').access_token;
    } catch (e) {
      return null;
    }
  }

  static removeHubApiToken() {
    Storage.removeItem('hubToken');
  }

  static async fetchHubApiToken() {
    const response = await RestClient.post(getEndPoint('hubToken'), {
      grant_type: config.get('grantType', ''),
      client_id: config.get('clientId', ''),
      scope: config.get('tokenScope', ''),
    });
    const expiresIn = Math.floor(response.expires_in * 4 / 5);
    Storage.setItem('hubToken', response, expiresIn);
    return response.access_token;
  }

  static async fetchHubTokenResetPassword() {
    const response = await RestClient.post(getEndPoint('hubToken'), {
      grant_type: config.get('grantType', ''),
      client_id: config.get('clientId', ''),
      scope: 'reset_password',
    });
    const expiresIn = Math.floor(response.expires_in * 4 / 5);
    Storage.setItem('hubTokenResetPassword', response, expiresIn);
    return response.access_token;
  }

  static getResetScopeToken() {
    try {
      return JSON.parse(window.localStorage.getItem('hubTokenResetPassword')).data.access_token;
    } catch (e) {
      return null;
    }
  }

  static getQueryParams() {
    let queryString = sessionStorage.getItem('queryString');
    queryString = queryString && queryString.charAt(0) === '?' ? queryString : '';
    return queryString;
  }
}
